/** Application state: session, settings, map list, sync status. */
import type { UserInfo } from '../../shared/api.ts';
import { MATERIALS, type ElevMeta, type TestStyle } from '../../shared/doc.ts';
import { getKV, setKV, type LocalMap } from '../store/idb.ts';
import { Store } from './store.ts';
import { setToken } from './api.ts';

export interface Session {
  token: string;
  user: UserInfo;
  /** The personal sign-in code used on this device (shown in Settings for signing in elsewhere). */
  code?: string;
}
export interface Settings {
  exportFormat: 'png' | 'jpeg';
  markupScale: number; // default for new maps
  fingerPansAfterPen: boolean;
  legendDefault: boolean;
  /** Anchor points being dragged snap to the plan's lines. */
  snapAnchors: boolean;
  /** How tests are labelled on new soils maps (the style last chosen on a map). */
  testStyle: TestStyle;
  /** The markup labels' words typed lately, most recent first (offered again). */
  labelPhrases: string[];
  /** The colour-only concrete material last chosen from the colours (offered at a tap beside the named ones; Concrete green at first). */
  otherMaterial: string;
  /** New maps show the pinpoints — tests' dots, shots' rings — where the leaders end (a map's own from its More menu). */
  dots: boolean;
  /** Where the number pad was last put over the plan (a share of the screen; the edge it settled against), if it was carried. */
  padAt?: { fx: number; fy: number; dock: 'left' | 'right' | null };
  /** Light or dark: as the system says, or the inspector's own choice (2026-10-23, Adam: "make sure that light/dark mode is user editable"). */
  appearance: 'system' | 'light' | 'dark';
  /** How elevation tags read on new soils maps, as Settings says: their colour (absent: the bright blue), word and unit. */
  elevDefaults: Pick<ElevMeta, 'prefix' | 'color' | 'unit'>;
}
export type SyncStatus = 'idle' | 'syncing' | 'synced' | 'pending' | 'offline' | 'error';

export interface AppState {
  ready: boolean;
  session: Session | null;
  settings: Settings;
  maps: LocalMap[];
  sync: SyncStatus;
  syncError: string | null;
  online: boolean;
  installed: boolean;
}

export const DEFAULT_SETTINGS: Settings = { exportFormat: 'png', markupScale: 1, fingerPansAfterPen: true, legendDefault: false, snapAnchors: true, testStyle: 'test', labelPhrases: [], elevDefaults: {}, otherMaterial: 'conc', dots: true, appearance: 'system' };

export const app = new Store<AppState>({
  ready: false,
  session: null,
  settings: DEFAULT_SETTINGS,
  maps: [],
  sync: 'idle',
  syncError: null,
  online: navigator.onLine,
  installed: matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true,
});

export async function loadPersistedState(): Promise<void> {
  const [session, settings] = await Promise.all([getKV<Session>('session'), getKV<Partial<Settings>>('settings')]);
  if (session?.token) setToken(session.token);
  const merged = { ...DEFAULT_SETTINGS, ...(settings ?? {}) };
  // (a list kept on the device is checked: anything else is no list)
  if (!Array.isArray(merged.labelPhrases)) merged.labelPhrases = [];
  if (typeof merged.elevDefaults !== 'object' || merged.elevDefaults === null) merged.elevDefaults = {};
  if (typeof merged.dots !== 'boolean') merged.dots = true;
  if (merged.appearance !== 'light' && merged.appearance !== 'dark') merged.appearance = 'system';
  const pa = merged.padAt;
  if (pa && !(typeof pa === 'object' && Number.isFinite(pa.fx) && Number.isFinite(pa.fy) && pa.fx >= 0 && pa.fx <= 1 && pa.fy >= 0 && pa.fy <= 1)) delete merged.padAt;
  if (typeof merged.otherMaterial !== 'string' || !MATERIALS.some((m) => m.swatchOnly && m.key === merged.otherMaterial)) merged.otherMaterial = 'conc';
  delete (merged as Partial<Settings> & { lastShape?: unknown }).lastShape;
  merged.labelPhrases = merged.labelPhrases.filter((t): t is string => typeof t === 'string' && t.length > 0 && t.length <= 80).slice(0, 8);
  app.set({ session: session ?? null, settings: merged });
  applyAppearance(merged.appearance);
}

/**
 * Light or dark, as chosen: the page's tokens (app.css, `data-theme`), the system's own controls
 * (`color-scheme`) and the browser's bar round the app (`theme-color`) all follow. "System" follows
 * the system, live.
 */
export function applyAppearance(a: Settings['appearance']): void {
  const root = document.documentElement;
  if (a === 'system') delete root.dataset.theme;
  else root.dataset.theme = a;
  const dark = a === 'dark' || (a === 'system' && matchMedia('(prefers-color-scheme: dark)').matches);
  for (const m of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    if (a === 'system') m.content = m.media.includes('dark') ? '#000000' : '#f2f2f7';
    else m.content = dark ? '#000000' : '#f2f2f7';
  }
}

export async function setSession(s: Session | null): Promise<void> {
  setToken(s?.token ?? null);
  app.set({ session: s });
  await setKV('session', s);
}

export async function updateSettings(patch: Partial<Settings>): Promise<void> {
  const settings = { ...app.state.settings, ...patch };
  app.set({ settings });
  if (patch.appearance) applyAppearance(patch.appearance);
  await setKV('settings', settings);
}

export function currentInitials(): string {
  return app.state.session?.user.initials ?? 'XX';
}
